import { SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { MultiSelect } from '~/components/ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Switch } from '~/components/ui/switch';
import { Textarea } from '~/components/ui/textarea';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { useTranslationLocales } from '~/hooks/useTranslationLocales';
import { cn } from '~/utils/cn';

import type { BrowseExportFilters } from '../../utils/export-browse-filters';
import {
  DEFAULT_BROWSE_EXPORT_FILTERS,
  isValidScoreText,
  MAX_PRODUCT_IDS,
  countActiveExportFilters,
  parseIdList,
  parseScore,
} from '../../utils/export-browse-filters';

interface BrowseFiltersPopoverProps {
  filters: BrowseExportFilters;
  onApply: (next: BrowseExportFilters) => void;
  /** Locales the run was translated into (with exportable products). Empty — the run has
   *  no translations, or it is META — hides the Language select. Internal users only. */
  languageOptions?: string[];
  /** Offer the product-id allow-list. Off on the catalog export, which refuses
   *  `productIds` (product ids change per batch; it filters by SKU instead). */
  showProductIds?: boolean;
  /**
   * Known category values with their row counts. Given, Categories is a
   * searchable multi-select of exactly these; absent (the batch page has no
   * endpoint that lists a run's categories), it stays free text.
   */
  categoryOptions?: { value: string; count: number }[];
  /** The trigger's text; defaults to "Filters". The catalog page names these "Row rules" to
   *  tell them apart from its grid filters, which only narrow the view. */
  triggerLabel?: string;
}

/** Radix Select forbids an empty-string item value, so "English" needs a sentinel. */
const ENGLISH = '__en__';

/**
 * The export filters — the ones that travel with the file, as opposed to the
 * search box and the header-row inputs, which only narrow the view.
 *
 * Draft-then-apply like the column picker, and for the same reason: every
 * committed change re-reads a page, a count and an estimate, so a score range
 * typed one digit at a time must not fire three requests.
 *
 * Categories and product ids are free text. There is no endpoint that lists a
 * run's categories, and a merchandiser's allow-list usually arrives as a pasted
 * spreadsheet column — so both accept any comma / newline separated text and
 * show what they parsed to as chips, which is the honest form of a picker we
 * cannot build yet.
 */
export function BrowseFiltersPopover({
  filters,
  onApply,
  languageOptions = [],
  showProductIds = true,
  categoryOptions,
  triggerLabel,
}: BrowseFiltersPopoverProps) {
  const { t } = useTranslation('exports');
  const [open, setOpen] = useState(false);
  const [successOnly, setSuccessOnly] = useState(filters.successOnly);
  const showLanguage = useCanTranslateContent() && languageOptions.length > 0;
  // Display names only; the offered set is the run's own locales.
  const locales = useTranslationLocales(showLanguage);
  const nameOf = (code: string) =>
    locales.data?.find((l) => l.code.toLowerCase() === code.toLowerCase())?.displayName ?? code;
  const [locale, setLocale] = useState(filters.locale);
  // The raw text of every typed input, kept separately from the parsed draft:
  // a trailing comma must not vanish under the cursor, and `0.` must survive
  // long enough to become `0.5` — re-rendering from the parsed number ate the
  // dot. Parsed once, on Apply.
  const [categoriesText, setCategoriesText] = useState('');
  const [productIdsText, setProductIdsText] = useState('');
  const [minScoreText, setMinScoreText] = useState('');
  const [maxScoreText, setMaxScoreText] = useState('');

  const activeCount = countActiveExportFilters(filters);

  /** Opening re-seeds the draft from the committed filters, so a cancelled
   *  edit cannot leak into the next open. */
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSuccessOnly(filters.successOnly);
      setCategoriesText(filters.categories.join(', '));
      setProductIdsText(filters.productIds.join('\n'));
      setMinScoreText(filters.minScore === undefined ? '' : String(filters.minScore));
      setMaxScoreText(filters.maxScore === undefined ? '' : String(filters.maxScore));
      setLocale(filters.locale);
    }
    setOpen(next);
  };

  const draftCategories = parseIdList(categoriesText);
  const draftProductIds = parseIdList(productIdsText);
  const tooManyIds = draftProductIds.length > MAX_PRODUCT_IDS;
  const draftMinScore = parseScore(minScoreText);
  const draftMaxScore = parseScore(maxScoreText);
  // A typed score outside 0–10 (or not a number) is an error to fix, never silently clamped:
  // a clamped -1 → 0 would apply a filter the operator did not ask for.
  const minInvalid = !isValidScoreText(minScoreText);
  const maxInvalid = !isValidScoreText(maxScoreText);
  const scoreOutOfRange = minInvalid || maxInvalid;
  const scoreInverted =
    !scoreOutOfRange &&
    draftMinScore !== undefined &&
    draftMaxScore !== undefined &&
    draftMinScore > draftMaxScore;
  const applyBlocked = tooManyIds || scoreInverted || scoreOutOfRange;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-9 cursor-pointer text-xs">
          <SlidersHorizontal aria-hidden="true" className="mr-1.5 size-3.5" />
          {triggerLabel ?? t('browse.filters.trigger')}
          {activeCount > 0 && (
            // `secondary` is the crimson token here, deliberately: these are the
            // filters that change what lands in the file.
            <Badge
              variant="secondary"
              className="pointer-events-none ml-1.5 text-[11px] font-normal"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      {/* Compact: one line per rule — label left, control right; the explanations live in
          (i) tooltips, and only an error takes a line of its own. */}
      <PopoverContent align="start" className="flex w-[min(26rem,92vw)] flex-col gap-3 p-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold">
            {triggerLabel ?? t('browse.filters.trigger')}
          </span>
          <InfoTooltip content={t('browse.filters.intro')} />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="browse-filter-success" className="cursor-pointer text-sm font-normal">
            {t('browse.successOnly')}
          </Label>
          <Switch
            id="browse-filter-success"
            checked={successOnly}
            onCheckedChange={setSuccessOnly}
            className="cursor-pointer"
          />
        </div>

        <fieldset className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <legend className="flex items-center gap-1.5 text-sm">
              {t('browse.filters.score')}
              <InfoTooltip content={t('browse.filters.scoreHint')} />
            </legend>
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={10}
                step={0.1}
                value={minScoreText}
                onChange={(event) => {
                  setMinScoreText(event.target.value);
                }}
                placeholder={t('browse.filters.scoreMin')}
                aria-label={t('browse.filters.scoreMin')}
                aria-invalid={minInvalid}
                className={cn(
                  'h-8 w-20 text-xs',
                  minInvalid && 'border-error focus-visible:ring-error',
                )}
              />
              <span className="text-muted-foreground text-xs">–</span>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={10}
                step={0.1}
                value={maxScoreText}
                onChange={(event) => {
                  setMaxScoreText(event.target.value);
                }}
                placeholder={t('browse.filters.scoreMax')}
                aria-label={t('browse.filters.scoreMax')}
                aria-invalid={maxInvalid}
                className={cn(
                  'h-8 w-20 text-xs',
                  maxInvalid && 'border-error focus-visible:ring-error',
                )}
              />
            </div>
          </div>
          {(scoreInverted || scoreOutOfRange) && (
            <p className="text-error text-right text-xs" role="alert">
              {scoreOutOfRange
                ? t('browse.filters.scoreOutOfRange')
                : t('browse.filters.scoreInverted')}
            </p>
          )}
        </fieldset>

        {showLanguage && (
          <div className="flex items-center justify-between gap-3">
            <Label
              htmlFor="browse-filter-locale"
              className="flex items-center gap-1.5 text-sm font-normal"
            >
              {t('browse.filters.language')}
              <InfoTooltip content={t('browse.filters.languageHint')} />
            </Label>
            <Select
              value={locale ?? ENGLISH}
              onValueChange={(v) => {
                setLocale(v === ENGLISH ? undefined : v);
              }}
            >
              <SelectTrigger id="browse-filter-locale" className="h-8 w-44 cursor-pointer text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ENGLISH} className="cursor-pointer text-xs">
                  {t('browse.filters.languageEnglish')}
                </SelectItem>
                {languageOptions.map((code) => (
                  <SelectItem key={code} value={code} className="cursor-pointer text-xs">
                    {nameOf(code)} ({code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="browse-filter-categories" className="shrink-0 text-sm font-normal">
              {t('browse.filters.categories')}
            </Label>
            <div className="min-w-0 flex-1 basis-0">
              {categoryOptions ? (
                <MultiSelect
                  options={[
                    ...categoryOptions.map((option) => ({
                      value: option.value,
                      label: `${option.value} (${option.count.toLocaleString()})`,
                    })),
                    // A committed value the current selection no longer has stays removable.
                    ...draftCategories
                      .filter((value) => !categoryOptions.some((option) => option.value === value))
                      .map((value) => ({ value, label: value })),
                  ]}
                  value={draftCategories}
                  onChange={(next) => {
                    setCategoriesText(next.join(', '));
                  }}
                  placeholder={t('browse.filters.categoriesPick')}
                  searchPlaceholder={t('browse.filters.categoriesSearch')}
                  className="text-xs"
                />
              ) : (
                <Input
                  id="browse-filter-categories"
                  value={categoriesText}
                  onChange={(event) => {
                    setCategoriesText(event.target.value);
                  }}
                  placeholder={t('browse.filters.categoriesPlaceholder')}
                  className="h-8 text-xs"
                />
              )}
            </div>
          </div>
          {!categoryOptions && draftCategories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {draftCategories.map((category) => (
                <Badge
                  key={category}
                  variant="outline"
                  className="pointer-events-none max-w-full truncate text-[11px] font-normal"
                >
                  {category}
                </Badge>
              ))}
            </div>
          )}
          {!categoryOptions && (
            <p className="text-muted-foreground text-xs">{t('browse.filters.categoriesHint')}</p>
          )}
        </div>

        {showProductIds && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="browse-filter-ids" className="text-sm font-normal">
              {t('browse.filters.productIds')}
            </Label>
            <Textarea
              id="browse-filter-ids"
              value={productIdsText}
              onChange={(event) => {
                setProductIdsText(event.target.value);
              }}
              placeholder={t('browse.filters.productIdsPlaceholder')}
              rows={3}
              className="font-mono text-xs"
            />
            <p className={cn('text-xs', tooManyIds ? 'text-error' : 'text-muted-foreground')}>
              {tooManyIds
                ? t('browse.filters.productIdsTooMany', { max: MAX_PRODUCT_IDS })
                : t('browse.filters.productIdsCount', { count: draftProductIds.length })}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 cursor-pointer text-xs"
            onClick={() => {
              setSuccessOnly(DEFAULT_BROWSE_EXPORT_FILTERS.successOnly);
              setCategoriesText('');
              setProductIdsText('');
              setMinScoreText('');
              setMaxScoreText('');
              setLocale(undefined);
            }}
          >
            <X aria-hidden="true" className="mr-1 size-3.5" />
            {t('browse.filters.reset')}
          </Button>
          <span className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 cursor-pointer text-xs"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('estimate.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              aria-disabled={applyBlocked}
              className={cn(
                'h-8 text-xs',
                applyBlocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
              onClick={() => {
                if (applyBlocked) return;
                onApply({
                  successOnly,
                  minScore: draftMinScore,
                  maxScore: draftMaxScore,
                  categories: draftCategories,
                  productIds: draftProductIds,
                  locale: showLanguage ? locale : filters.locale,
                });
                setOpen(false);
              }}
            >
              {t('columns.apply')}
            </Button>
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
